import { AuthContextType } from '../contexts/AuthContext';
import React from 'react';
import { FLOW_SERVICE_BASE_URL } from '../../ApiConfig';

/**
 * API Client with automatic token injection and refresh
 * Handles both refresh_token and direct token flows transparently
 */

class ApiClient {
  private getTokenFunction: (() => string | null) | null = null;
  private checkTokenExpiryFunction: (() => boolean) | null = null;
  private refreshTokensFunction: (() => Promise<boolean>) | null = null;
  private getUserEmailFunction: (() => string | null) | null = null;
  private isInitialized = false;
  private initializationResolvers: Array<() => void> = [];

  /**
   * Set the token management functions
   * These functions will be called by the API client when needed
   *
   * @param getToken - Function to retrieve the current token from memory
   * @param checkExpiry - Function to check if current token is expired
   * @param refreshTokens - Function to refresh expired tokens (only for exchange-token flow)
   * @param getUserEmail - Function to get user email for x-aptean-user header
   */
  setTokenProvider(
    getToken: () => string | null,
    checkExpiry: () => boolean,
    refreshTokens: () => Promise<boolean>,
    getUserEmail: () => string | null,
  ) {
    this.getTokenFunction = getToken;
    this.checkTokenExpiryFunction = checkExpiry;
    this.refreshTokensFunction = refreshTokens;
    this.getUserEmailFunction = getUserEmail;
    this.isInitialized = true;
    
    // Resolve all waiting promises
    this.initializationResolvers.forEach((resolve) => resolve());
    this.initializationResolvers = [];
  }

  /**
   * Wait for API client initialization
   * Returns immediately if already initialized
   * Otherwise, returns a promise that resolves when setTokenProvider is called
   */
  private async waitForInitialization(): Promise<void> {
    if (this.isInitialized) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      this.initializationResolvers.push(resolve);
      
      // Timeout after 5 seconds
      setTimeout(() => {
        reject(new Error('API client initialization timeout. useInitializeApiClient() was not called.'));
      }, 5000);
    });
  }

  /**
   * Check if refresh capability is available
   */
  hasRefreshCapability(): boolean {
    return this.refreshTokensFunction !== null;
  }

  /**
   * Check if URL is a Flow Service URL that requires x-aptean-user header
   */
  private isFlowServiceUrl(url: string): boolean {
    return url.startsWith(FLOW_SERVICE_BASE_URL);
  }

  /**
   * Fetch wrapper that automatically injects authorization token
   * Handles token expiration and automatic refresh
   * Token source depends on initial auth flow:
   * - refresh_token flow: Uses iamAccessToken from exchange-token response
   * - direct token flow: Uses original token from URL
   */
  async fetch(url: string, options: RequestInit = {}): Promise<Response> {
    // Wait for initialization to complete
    await this.waitForInitialization();

    // Check if token is expired
    if (this.checkTokenExpiryFunction?.()) {
      // Try to refresh only if refresh function is available (exchange-token flow)
      if (this.refreshTokensFunction) {
        console.log('Token expired, attempting refresh...');
        const refreshed = await this.refreshTokensFunction();
        if (!refreshed) {
          throw new Error('Session expired. Please log in again.');
        }
      } else {
        // check-token flow: no refresh capability
        throw new Error('Session expired. Please log in again.');
      }
    }

    const token = this.getTokenFunction?.();
    
    if (!token) {
      throw new Error('No authentication token available');
    }

    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
      Authorization: `Bearer ${token}`,
    };

    // Add x-aptean-user header for Flow Service URLs
    if (this.isFlowServiceUrl(url)) {
      const userEmail = this.getUserEmailFunction?.();
      if (userEmail) {
        headers['x-aptean-user'] = userEmail;
      }
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });    // Handle 401 - token might be invalid
    if (response.status === 401) {
      console.log('Received 401 Unauthorized');
      
      // Try to refresh token only if refresh function exists (exchange-token flow)
      if (this.refreshTokensFunction) {
        console.log('Attempting token refresh...');
        const refreshed = await this.refreshTokensFunction();
        if (refreshed) {
          // Retry the request with new token
          const newToken = this.getTokenFunction?.();
          if (newToken) {
            const retryHeaders: Record<string, string> = {
            ...(options.headers as Record<string, string>),
            Authorization: `Bearer ${newToken}`,
          };

            // Add x-aptean-user header for Flow Service URLs on retry
            if (this.isFlowServiceUrl(url)) {
              const userEmail = this.getUserEmailFunction?.();
              if (userEmail) {
                retryHeaders['x-aptean-user'] = userEmail;
              }
            }

            return fetch(url, {
              ...options,
              headers: retryHeaders,
            });
          }
        }
      }

      // For check-token flow or failed refresh
      throw new Error(
        'Authentication failed. Session expired, please log in again.',
      );
    }

    return response;
  }

  /**
   * Get the current token (useful for non-fetch calls)
   */
  getAuthToken(): string | null {
    return this.getTokenFunction?.() || null;
  }

  /**
   * Get authorization header object
   */
  getAuthHeader(): { Authorization: string } | null {
    const token = this.getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : null;
  }
}

export const apiClient = new ApiClient();

/**
 * Hook to initialize API client with token management functions
 * Call this in your root App component or AtlasmapProvider
 */
export function useInitializeApiClient(authContext: AuthContextType) {
  React.useMemo(() => {
    apiClient.setTokenProvider(
      authContext.getIamAccessToken,
      authContext.isTokenExpired,
      authContext.refreshTokens,
      () => authContext.getUserInfo()?.email || '',
    );
  }, [
    authContext.getIamAccessToken,
    authContext.isTokenExpired,
    authContext.refreshTokens,
    authContext.getUserInfo,
  ]);
}
